import { useMemo, type PropsWithChildren } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { QueryClientProvider } from "@tanstack/react-query";
import { AppErrorBoundary } from "@/components/feedback/AppErrorBoundary";
import { DialogProvider } from "@/components/ui/DialogProvider";
import { createAppTheme } from "@/theme/theme";
import { ThemeModeProvider, useThemeMode } from "@/theme/ThemeModeContext";
import { AuthProvider } from "@/api/auth";
import { queryClient } from "@/api/queryClient";
import { SocketProvider } from "@/hooks/useSocket";

function ThemedProviders({ children }: PropsWithChildren) {
  const { themeMode } = useThemeMode();
  const appTheme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <AppErrorBoundary>{children}</AppErrorBoundary>
    </ThemeProvider>
  );
}

export function AppProviders({ children }: PropsWithChildren) {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SocketProvider>
          <ThemeModeProvider>
            <ThemedProviders>
              <DialogProvider>{children}</DialogProvider>
            </ThemedProviders>
          </ThemeModeProvider>
        </SocketProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
